import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TerminalIcon from '@mui/icons-material/Terminal';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import { useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { AppArt, AppArtBucket, AppArtStatus } from '../types';

/* Os eixos do gerador. Em branco significa RODÍZIO: o script escolhe o que
   menos saiu nas rodadas anteriores daquela rede, então pedir de novo devolve
   arte diferente sem ninguém precisar mexer aqui. Travar um eixo não desliga a
   variação — desloca ela para os de dentro (qual foto, enquadramento,
   tipografia, direção). */
const ESTILOS = [
  { valor: 'foto', rotulo: 'Foto real', ajuda: 'a fachada do posto como fotografia' },
  { valor: 'degrade', rotulo: 'Degradê', ajuda: 'campo de cor da marca, sem foto' },
  { valor: 'ilustrado', rotulo: 'Ilustrado', ajuda: 'a fachada posterizada, lendo como desenho' },
];

const IDENTIDADES = [
  { valor: 'nenhuma', rotulo: 'Nenhuma (o app desenha)', ajuda: 'o asset é só fundo' },
  { valor: 'logo', rotulo: 'Logo no asset', ajuda: 'o PNG já traz a logo gravada' },
  { valor: 'completa', rotulo: 'Logo + manchete', ajuda: 'o PNG traz logo e slogan' },
];

const PALETAS = [
  { valor: 'marca', rotulo: 'Cor da marca', ajuda: 'o campo é a cor da rede escurecida' },
  { valor: 'acento', rotulo: 'Cor secundária', ajuda: 'só quando a rede tem secundária' },
  { valor: 'neutro', rotulo: 'Neutro', ajuda: 'campo grafite com a marca cheia nos acentos' },
];

/**
 * O gerador não roda no servidor: ele usa Playwright e a CLI do Claude
 * autenticada na máquina de quem opera. Então a tela não executa nada — ela
 * monta o comando exato daquela rede, com as opções escolhidas, para colar no
 * terminal. É a diferença entre "roda o script lá" e um comando que já sabe o
 * chainId.
 *
 * O comando sai como script de npm e SEM o `cd`: quem opera roda dentro do
 * checkout do gerador, e um caminho chutado por esta tela é justamente o que
 * daria errado na máquina de quem clonou em outro lugar.
 */
const GenerateCommandButton = ({ highlighted }: { highlighted?: boolean }) => {
  const record = useRecordContext<AppArt>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [artes, setArtes] = useState('');
  const [estilo, setEstilo] = useState('');
  const [identidade, setIdentidade] = useState('');
  const [paleta, setPaleta] = useState('');
  const [foto, setFoto] = useState('');
  const [semMemoria, setSemMemoria] = useState(false);
  const [force, setForce] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  /* Ligado por padrão, por decisão de quem opera. A avaliação final custa caro
     — ela reprova e o laço repete, e cada tentativa são duas chamadas do
     agente, o que faz uma rodada passar de 40 minutos. Com ela desligada a
     primeira arte é a arte. As barreiras do HTML continuam valendo: script,
     recurso externo e cor fora dos tokens seguem barrados. */
  const [semAvaliacao, setSemAvaliacao] = useState(true);

  if (!record) { return null; }

  // Quantas fotos a rede mandou. O seletor de foto só faz sentido com mais de
  // uma — e é justamente com várias que o rodízio entrega mais.
  const fotos = (record.files || []).filter(file => file.bucket === AppArtBucket.stationPhoto);
  const semFoto = fotos.length === 0;
  // `foto` e `ilustrado` precisam de fotografia. Com a rede sem nenhuma, o
  // script avisa e cai para degradê — melhor dizer isso aqui do que deixar a
  // pessoa montar um comando que não faz o que ela pediu.
  const estiloImpossivel = semFoto && (estilo === 'foto' || estilo === 'ilustrado');

  const flags = [
    semAvaliacao ? '--sem-avaliacao' : '',
    `--chain ${record.chainId}`,
    // Motor fixo: é o Claude que desenha. O determinístico continua no script
    // como rede de proteção quando a CLI falha, não como opção de quem pede.
    '--motor agente',
    artes ? `--artes ${artes}` : '',
    estilo ? `--estilo ${estilo}` : '',
    identidade ? `--identidade ${identidade}` : '',
    paleta ? `--paleta ${paleta}` : '',
    foto ? `--foto ${foto}` : '',
    semMemoria ? '--sem-memoria' : '',
    force ? '--force' : '',
    dryRun ? '--dry-run' : '',
  ].filter(Boolean).join(' ');

  // O `--` é do npm: sem ele as flags são comidas pelo próprio npm e nunca
  // chegam no script.
  const command = `npm run gerar -- ${flags}`;

  const copy = () => {
    copyToClipboard(command);
    notify('Comando copiado — cole no terminal.', { type: 'info' });
  };

  const draft = record.status === AppArtStatus.draft;

  return (
    <>
      <Tooltip title={draft
        ? 'A rede ainda está em rascunho: envie para a geração antes de rodar o gerador.'
        : 'Monta o comando do gerador para esta rede'}>
        <span>
          <Button
            size="small"
            variant={highlighted ? 'contained' : 'outlined'}
            startIcon={<TerminalIcon />}
            onClick={() => setOpen(true)}
          >
            Gerar modelos
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gerar modelos — {record.chainName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rode dentro do <code>pontuax-art-generator</code>: o gerador usa o
            Playwright e a CLI do Claude autenticada na sua máquina. Ele lê o
            material desta rede, sobe os modelos e move o status para
            “Modelos gerados” sozinho.
          </Typography>

          {draft && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              Esta rede ainda está em rascunho. O gerador funciona com o
              chainId mesmo assim, mas o normal é enviar para a geração antes —
              é o envio que trava o material.
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              select
              size="small"
              label="Quantas artes"
              value={artes}
              onChange={event => setArtes(event.target.value)}
              sx={{ minWidth: 180 }}
              helperText="em branco = automático"
            >
              <MenuItem value="">automático</MenuItem>
              {[1, 2, 3, 4, 5, 6].map(n => (
                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
              ))}
            </TextField>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={force} onChange={(_, v) => setForce(v)} />}
                label="Usar a foto mesmo reprovada (--force)"
              />
              <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={(_, v) => setDryRun(v)} />}
                label="Só gerar local, sem subir (--dry-run)"
              />
              {/* Desmarcar liga a medição — e com ela o laço de tentativas, que
                  é o que faz a rodada demorar. Vale quando a arte vai para o
                  cliente e ninguém vai olhar peça por peça. */}
              <Tooltip title="Marcado (padrão): sai uma arte por modelo, rápido, sem medir contraste da manchete, calma no topo e na base, motivo e semelhança com as rodadas anteriores. Desmarque para o gerador medir e repetir até passar — é mais lento, e pode levar mais de 40 minutos.">
                <FormControlLabel
                  control={<Checkbox checked={semAvaliacao} onChange={(_, v) => setSemAvaliacao(v)} />}
                  label="Pular a avaliação final (--sem-avaliacao)"
                />
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="overline" color="text.secondary">Variação</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Deixe tudo em <strong>rodízio</strong> no uso normal: o gerador olha o que já
            fez para esta rede e escolhe o que menos saiu, então rodar de novo
            devolve arte diferente sem mexer em nada aqui. Travar um eixo não desliga
            a variação — ela passa para qual foto entra, enquadramento, tipografia e
            direção de arte.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              select size="small" label="Estilo do fundo" value={estilo}
              onChange={event => setEstilo(event.target.value)}
              sx={{ minWidth: 210 }}
              error={estiloImpossivel}
              helperText={estiloImpossivel
                ? 'esta rede não tem foto — o script cai para degradê'
                : (ESTILOS.find(e => e.valor === estilo)?.ajuda || 'rodízio entre os possíveis')}
            >
              <MenuItem value="">rodízio (recomendado)</MenuItem>
              {ESTILOS.map(e => (
                <MenuItem key={e.valor} value={e.valor} disabled={semFoto && e.valor !== 'degrade'}>
                  {e.rotulo}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select size="small" label="O que o asset grava" value={identidade}
              onChange={event => setIdentidade(event.target.value)}
              sx={{ minWidth: 230 }}
              helperText={IDENTIDADES.find(i => i.valor === identidade)?.ajuda || 'rodízio entre os três'}
            >
              <MenuItem value="">rodízio (recomendado)</MenuItem>
              {IDENTIDADES.map(i => (
                <MenuItem key={i.valor} value={i.valor}>{i.rotulo}</MenuItem>
              ))}
            </TextField>

            <TextField
              select size="small" label="Paleta" value={paleta}
              onChange={event => setPaleta(event.target.value)}
              sx={{ minWidth: 210 }}
              helperText={PALETAS.find(p => p.valor === paleta)?.ajuda || 'rodízio entre as viáveis'}
            >
              <MenuItem value="">rodízio (recomendado)</MenuItem>
              {PALETAS.map(p => (
                <MenuItem key={p.valor} value={p.valor} disabled={p.valor === 'acento' && !record.secondaryColor}>
                  {p.rotulo}
                </MenuItem>
              ))}
            </TextField>

            {fotos.length > 1 && (
              <TextField
                select size="small" label="Qual foto do posto" value={foto}
                onChange={event => setFoto(event.target.value)}
                sx={{ minWidth: 200 }}
                helperText={`a rede mandou ${fotos.length}`}
              >
                <MenuItem value="">rodízio entre as {fotos.length}</MenuItem>
                {fotos.map((_, indice) => (
                  <MenuItem key={indice} value={String(indice + 1)}>
                    foto {indice + 1}{indice === 0 ? ' (a melhor)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Tooltip title="O gerador guarda o que já fez para esta rede e evita repetir. Marcar isto faz ele sortear do zero — serve para reproduzir uma rodada antiga, não para o uso normal.">
            <FormControlLabel
              control={<Checkbox checked={semMemoria} onChange={(_, v) => setSemMemoria(v)} />}
              label="Ignorar o histórico da rede (--sem-memoria)"
            />
          </Tooltip>

          <Box
            sx={{
              mt: 2,
              p: 2, borderRadius: 1, backgroundColor: 'grey.900', color: 'grey.100',
              fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {command}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Sem <code>--chain</code>, o mesmo script processa a fila inteira — todas as
            redes em “Na fila da geração” e em “Nova rodada pedida”.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={copy}>
            Copiar comando
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateCommandButton;
